import { DateTime } from 'luxon'

export const PLACEHOLDER = '—'

const toDateTime = (value: string | Date | null | undefined): DateTime | null => {
  if (!value) return null

  const dateTime =
    value instanceof Date ? DateTime.fromJSDate(value) : DateTime.fromISO(value, { setZone: true })

  return dateTime.isValid ? dateTime : null
}

export const formatDateDefault = (value: string | Date | null | undefined): string =>
  toDateTime(value)?.toFormat('dd/MM/yyyy') ?? PLACEHOLDER

export const formatDateTimeDefault = (value: string | Date | null | undefined): string =>
  toDateTime(value)?.toFormat('dd/MM/yyyy HH:mm') ?? PLACEHOLDER

export const toApiDate = (value: string | Date | null | undefined): string | undefined =>
  toDateTime(value)?.toUTC().toISO() ?? undefined

export const fimDoDia = (value: Date): Date => DateTime.fromJSDate(value).endOf('day').toJSDate()

export const formatRelative = (value: string | Date | null | undefined): string =>
  toDateTime(value)?.setLocale('pt-BR').toRelative() ?? PLACEHOLDER

export const removeMascara = (value: string | null | undefined): string =>
  (value ?? '').replace(/\D/g, '')

export const formatCpf = (value: string | null | undefined): string => {
  const digitos = removeMascara(value)
  if (digitos.length !== 11) return value || PLACEHOLDER
  return digitos.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4')
}

export const formatCnpj = (value: string | null | undefined): string => {
  const digitos = removeMascara(value)
  if (digitos.length !== 14) return value || PLACEHOLDER
  return digitos.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5')
}

export const formatCpfCnpj = (value: string | null | undefined): string => {
  if (!value) return PLACEHOLDER

  const digitos = removeMascara(value)
  if (digitos.length === 11) return formatCpf(value)
  if (digitos.length === 14) return formatCnpj(value)

  return value
}

export const formatTelefone = (
  value: string | null | undefined,
  ddi?: string | null,
): string => {
  const digitos = removeMascara(value)
  if (!digitos) return PLACEHOLDER

  const digitosDdi = removeMascara(ddi)
  const prefixo = digitosDdi ? `+${digitosDdi} ` : ''
  if (digitos.length === 11) {
    return `${prefixo}${digitos.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3')}`
  }
  if (digitos.length === 10) {
    return `${prefixo}${digitos.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3')}`
  }

  return `${prefixo}${value}`
}

export const formatNumero = (value: number | null | undefined): string =>
  value === null || value === undefined ? PLACEHOLDER : new Intl.NumberFormat('pt-BR').format(value)

export const formatMoeda = (value: number | null | undefined): string =>
  value === null || value === undefined
    ? PLACEHOLDER
    : new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value)

export const base64ParaBlob = (base64: string): Blob => {
  const [prefixo, conteudo] = base64.includes(',') ? base64.split(',') : [null, base64]
  const tipo = prefixo ? /^data:(.*?);base64$/i.exec(prefixo)?.[1] : undefined

  const binario = atob(conteudo)
  const bytes = new Uint8Array(binario.length)

  for (let indice = 0; indice < binario.length; indice += 1) {
    bytes[indice] = binario.charCodeAt(indice)
  }

  return new Blob([bytes], tipo ? { type: tipo } : undefined)
}

export const downloadBlob = (blob: Blob, filename: string): void => {
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

export const orDash = (value: string | number | null | undefined): string => {
  if (value === null || value === undefined) return PLACEHOLDER
  const texto = String(value).trim()
  return texto === '' ? PLACEHOLDER : texto
}
